import { QUALITY_SCORE, type SupportQuality } from "./quality.js";

export type MasteryStatus =
  | "NOT_STARTED"
  | "INTRODUCED"
  | "LEARNING"
  | "ALMOST"
  | "SECURE"
  | "REVIEW_DUE"
  | "NEEDS_REVIEW";

export const CHILD_STATUS: Record<MasteryStatus, string> = {
  NOT_STARTED: "Не начато",
  INTRODUCED: "Знакомимся",
  LEARNING: "Учусь",
  ALMOST: "Почти умею",
  SECURE: "Закреплено",
  REVIEW_DUE: "Пора повторить",
  NEEDS_REVIEW: "Пора повторить",
};

export interface AttemptEvidence {
  quality: SupportQuality;
  itemId: string;
  sessionId: string;
  studyDate: string;
  createdAt: Date;
  firstAttempt: boolean;
}

export interface MasteryProjection {
  status: MasteryStatus;
  score: number;
  observations: number;
  distinctItems: number;
  distinctSessions: number;
  distinctDates: number;
  q4Count: number;
}

function eligible(attempts: AttemptEvidence[]): AttemptEvidence[] {
  return attempts.filter((a) => a.firstAttempt).sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
}

export function weightedScore(window: AttemptEvidence[]): number {
  const last8 = window.slice(-8);
  if (last8.length === 0) return 0;
  const newest4 = last8.slice(-4);
  const older = last8.slice(0, Math.max(0, last8.length - 4));
  let num = 0;
  let den = 0;
  for (const a of older) {
    num += QUALITY_SCORE[a.quality];
    den += 1;
  }
  for (const a of newest4) {
    num += QUALITY_SCORE[a.quality] * 2;
    den += 2;
  }
  return den === 0 ? 0 : (num / den) * 100;
}

export function projectMastery(attempts: AttemptEvidence[], now = new Date()): MasteryProjection {
  const ev = eligible(attempts);
  if (ev.length === 0) {
    return {
      status: "NOT_STARTED",
      score: 0,
      observations: 0,
      distinctItems: 0,
      distinctSessions: 0,
      distinctDates: 0,
      q4Count: 0,
    };
  }

  const score = weightedScore(ev);
  const items = new Set(ev.map((a) => a.itemId));
  const sessions = new Set(ev.map((a) => a.sessionId));
  const dates = new Set(ev.map((a) => a.studyDate));
  const q4Count = ev.filter((a) => a.quality === "Q4").length;
  const last4 = ev.slice(-4);
  const last3 = ev.slice(-3);
  const last2 = ev.slice(-2);
  const last4q4 = last4.filter((a) => a.quality === "Q4").length;
  const last2q4 = last2.filter((a) => a.quality === "Q4").length === last2.length && last2.length >= 2;
  const first = ev[0]!;
  const delayedQ4 = ev.some(
    (a) => a.quality === "Q4" && a.createdAt.getTime() - first.createdAt.getTime() >= 7 * 86_400_000,
  );
  const freshQ0 = last3.filter((a) => a.quality === "Q0").length;

  let status: MasteryStatus = "INTRODUCED";
  if (ev.length >= 2) status = "LEARNING";

  const almost =
    ev.length >= 6 &&
    items.size >= 3 &&
    sessions.size >= 2 &&
    score >= 80 &&
    last4q4 >= 3;

  const secure =
    ev.length >= 8 &&
    items.size >= 4 &&
    dates.size >= 3 &&
    score >= 85 &&
    q4Count >= 6 &&
    last2q4 &&
    delayedQ4;

  if (secure) status = "SECURE";
  else if (almost) status = "ALMOST";

  if (status === "SECURE" && freshQ0 >= 2) status = "NEEDS_REVIEW";

  void now;
  return {
    status,
    score,
    observations: ev.length,
    distinctItems: items.size,
    distinctSessions: sessions.size,
    distinctDates: dates.size,
    q4Count,
  };
}
